/**
 * D-EZ-105 — THE COMMISSIONER ROLLS THE BIG MOMENTS.
 *
 * One turn at the table, at "You roll the big ones". The turn's recorded throws
 * are walked in order:
 *
 *   • a quiet throw resolves itself at once — no dice motion, one readable line
 *     in the "Meanwhile" ticker;
 *   • a pivotal throw (the turn's `TableMoment`s, planned by
 *     `presentation/stakes/stakes.ts` from what the table has already shown)
 *     STOPS: the stakes card comes up and waits for the commissioner's Roll
 *     (button, Space or Enter). Then the SAME recorded faces land — this
 *     component never draws a die — and one bold line says what it meant.
 *
 * TWO BEATS. When the WHITE die picks the defender (a run at the top seven),
 * the commissioner throws the white die first; the card for the face that
 * landed (`moment.beats[white]`, all six built before the throw) comes up; then
 * he throws red and blue.
 *
 * "Skip to the cars" (and Escape) still ends the turn at once; "To the flag"
 * closes the whole table from the shell. Nothing here can change a result.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WoodenDice from './WoodenDice.tsx';
import StakesCard, { isRollKey } from './StakesCard.tsx';
import { stripCitations, useShowCitations } from '../lib/prefs.ts';
import { followOnMs, resultMs, type TableHold, type TableRoll } from '../presentation/raceDice.ts';
import type { TableMoment } from '../presentation/stakes/stakes.ts';
import TableCars, { involvedAcross, type TableField } from './TableCars.tsx';

/** How long the after-line holds before the race runs on by itself. */
export const RESULT_HOLD_MS = 2600;
/** A short pause so the ticker can be read before the next stakes card. */
export const AUTO_STEP_MS = 420;
/** Each follow-on throw of a moment (the problem after triples, the PFA defence, the escape checks). */
export const FOLLOW_ON_MS = 420;

/**
 * auto   — quiet throws resolving themselves
 * ready  — the stakes card, waiting for the Roll
 * white  — (two beats) the white die is down
 * ready2 — (two beats) the card for the white face, waiting for red and blue
 * throw  — the commissioner's dice are down (then any follow-on throws)
 * result — the after-line
 */
type Mode = 'auto' | 'ready' | 'white' | 'ready2' | 'throw' | 'result' | 'done';

export interface CommissionerTableProps {
  turn: number;
  rolls: TableRoll[];
  moments: readonly TableMoment[];
  /** The race's commissioner throws before this turn, to number this turn's. */
  thrownBefore: number;
  animated: boolean;
  sound: boolean;
  /** Table preferences → Time to read each roll (7 Oct 2026). Default steady. */
  hold?: TableHold;
  variation: number;
  /** The after-line for a moment — reads the outcome, so only called once it has landed. */
  resultFor: (moment: TableMoment) => string;
  /** The result goes out on the race radio. */
  onCall?: (moment: TableMoment, line: string) => void;
  onComplete: () => void;
  /** The cars going into the turn — for the driver cards under the dice (7 Oct 2026). */
  field?: TableField;
}

/** The white die of a throw, when the moment is played in two beats. */
export function whiteDieOf(moment: TableMoment, roll: TableRoll | undefined) {
  if (!moment.beats || !roll) return null;
  return roll.dice.find((d) => d.role === 'white') ?? null;
}

export default function CommissionerTable(props: CommissionerTableProps) {
  const { rolls, moments, animated, sound, variation } = props;
  const hold = props.hold ?? 'steady';
  const citations = useShowCitations();
  const ordered = useMemo(() => [...moments].sort((a, b) => a.start - b.start), [moments]);
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState<Mode>('auto');
  const [throwAt, setThrowAt] = useState(0);
  const [landed, setLanded] = useState(false);
  const [ticker, setTicker] = useState<string[]>([]);
  const [result, setResult] = useState('');
  const done = useRef(false);
  const felt = useRef<HTMLDivElement>(null);
  const complete = useRef(props.onComplete);
  complete.current = props.onComplete;
  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    setMode('done');
    complete.current();
  }, []);

  const read = (r: TableRoll | undefined) => {
    // The Bottom Group Churn's throws are never read one by one (Howard, 5 Oct
    // 2026): the first carries the whole shuffle in one plain line, the rest say nothing.
    const line = r?.churn ? (r.churnLine ?? '') : (r?.outcome ?? '');
    return citations ? line : stripCitations(line);
  };

  // ---- quiet throws: resolve at once, then stop at the next moment ----------
  useEffect(() => {
    if (mode !== 'auto') return;
    const next = ordered.find((m) => m.start >= index) ?? null;
    const stopAt = next ? next.start : rolls.length;
    const lines = rolls.slice(index, stopAt).map(read).filter(Boolean);
    if (lines.length) setTicker((t) => [...t, ...lines].slice(-3));
    const timer = window.setTimeout(
      () => {
        if (next) {
          setIndex(next.start);
          setMode('ready');
        } else finish();
      },
      lines.length || index > 0 ? AUTO_STEP_MS : 60,
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, index]);

  const active = mode === 'auto' || mode === 'done' ? null : (ordered.find((m) => m.start === index) ?? null);
  const white = active ? whiteDieOf(active, rolls[active.start]) : null;
  const twoBeat = Boolean(white);

  const roll = useCallback(() => {
    if (!active) return;
    if (mode === 'ready' && twoBeat) {
      setLanded(!animated);
      setMode('white');
    } else if (mode === 'ready' || mode === 'ready2') {
      setThrowAt(active.start);
      setLanded(!animated);
      setMode('throw');
    }
  }, [mode, active, animated, twoBeat]);

  const carryOn = useCallback(() => {
    if (mode !== 'result' || !active) return;
    setIndex(active.end);
    setResult('');
    setMode('auto');
  }, [mode, active]);

  // ---- two beats: the white die has landed; its card comes up --------------
  useEffect(() => {
    if (mode !== 'white' || !landed) return;
    const timer = window.setTimeout(() => {
      setLanded(false);
      setMode('ready2');
    }, 450);
    return () => window.clearTimeout(timer);
  }, [mode, landed]);

  // ---- the commissioner's throw lands, then any follow-on dice of the moment
  useEffect(() => {
    if (mode !== 'throw' || !active || !landed) return;
    const last = throwAt >= active.end - 1;
    const timer = window.setTimeout(
      () => {
        if (!last) {
          setThrowAt((n) => n + 1);
          return;
        }
        const line = props.resultFor(active);
        setResult(line);
        setMode('result');
        props.onCall?.(active, line);
      },
      throwAt === active.start ? 520 : hold === 'quick' ? FOLLOW_ON_MS : followOnMs(hold),
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, throwAt, landed, active]);

  // ---- the result holds, then the race runs on --------------------------------
  useEffect(() => {
    if (mode !== 'result' || !active) return;
    // 7 Oct 2026 — the result holds as long as Table preferences says (null =
    // until "Race on"). Quick is the old RESULT_HOLD_MS.
    const ms = resultMs(hold, result, involvedAcross(rolls.slice(active.start, active.end)).length);
    if (ms === null) return;
    const timer = window.setTimeout(carryOn, hold === 'quick' ? RESULT_HOLD_MS : ms);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, carryOn, hold, result]);

  // ---- keys: Space/Enter throws (or runs on); Escape skips to the cars --------
  // On the window: the commissioner may have clicked the board, and the shell's
  // own Space (run/pause) stands down while a table is open.
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        finish();
        return;
      }
      if (!isRollKey(event)) return;
      // A focused button answers its own Space/Enter with a click.
      if (tag === 'BUTTON') return;
      if (mode !== 'ready' && mode !== 'ready2' && mode !== 'result') return;
      event.preventDefault();
      if (mode === 'result') carryOn();
      else roll();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [mode, roll, carryOn, finish]);

  // The dice are the thing to watch once he has thrown (on a phone they sit under the card).
  useEffect(() => {
    if (mode !== 'white' && mode !== 'throw') return;
    try {
      felt.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      /* no layout (tests) */
    }
  }, [mode]);

  const number = active ? props.thrownBefore + ordered.indexOf(active) + 1 : undefined;
  const beatCard = active && white && mode !== 'ready' && mode !== 'white' ? (active.beats?.[white.face] ?? active.card) : active?.card;
  const cue = active ? rolls[mode === 'throw' ? throwAt : active.start] : undefined;
  const first = mode === 'throw' && active !== null && throwAt === active.start;
  // What is on the felt: the white die alone, then red and blue, then all three.
  const shown =
    mode === 'white' && white
      ? [white]
      : first && twoBeat && cue
        ? cue.dice.filter((d) => d.role !== 'white')
        : (cue?.dice ?? []);
  const stage = mode === 'ready' || mode === 'ready2' ? 'ready' : mode === 'result' ? 'result' : 'rolling';
  const rollingNow = (mode === 'white' || first) && animated && !landed;

  return (
    <div className="commish" role="region" aria-label={`Turn ${props.turn} at the table`}>
      {active && beatCard ? (
        <>
          <StakesCard
            card={beatCard}
            number={number}
            stage={stage}
            result={result}
            rollLabel={mode === 'ready' && twoBeat ? 'Roll the white die' : mode === 'ready2' ? 'Roll red and blue' : 'Roll'}
            onRoll={roll}
            onContinue={carryOn}
          />
          {(mode === 'white' || mode === 'ready2' || mode === 'throw' || mode === 'result') && cue && (
            <div className="commish__dice" ref={felt}>
              {cue.selection !== undefined ? (
                <div className="tablepick">Selection {cue.selection}</div>
              ) : (
                <WoodenDice
                  key={`${mode}-${throwAt}`}
                  dice={mode === 'ready2' && white ? [white] : shown}
                  rolling={rollingNow}
                  variation={variation + throwAt}
                  sound={sound}
                  onSettled={() => setLanded(true)}
                />
              )}
              {mode === 'throw' && !first && <p className="commish__follow">{cue.title}</p>}
            </div>
          )}
        </>
      ) : (
        <p className="commish__quiet" role="status">
          The quiet throws resolve themselves…
        </p>
      )}
      {/* 7 Oct 2026 — the cars this throw is about. While it is being thrown,
          only the drivers it is FOR; once the result is read, everyone the
          moment's dice named. Before the throw the stakes card already says who. */}
      {props.field && active && cue && mode !== 'ready' && (
        <TableCars
          key={`${active.key}-${mode === 'result' ? 'read' : throwAt}`}
          cars={props.field.cars}
          teams={props.field.teams}
          {...(mode === 'result'
            ? { ids: involvedAcross(rolls.slice(active.start, active.end)), label: 'Involved' }
            : { ids: cue.subjectIds, label: 'On this roll' })}
        />
      )}
      {ticker.length > 0 && mode !== 'result' && (
        <div className="commish__ticker" aria-live="polite">
          <p className="commish__tickhead">Meanwhile</p>
          {ticker.map((line, i) => (
            <p key={`${i}-${line.slice(0, 20)}`} className="commish__tick">
              {line}
            </p>
          ))}
        </div>
      )}
      <div className="tablework__actions">
        <button type="button" className="btn btn--quiet" onClick={finish}>
          Skip to the cars
        </button>
      </div>
    </div>
  );
}
